import { Facebook, Instagram, ArrowUp } from "lucide-react";

function WhatsAppIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}
const logo = new URL("../../assets/mononites-logo.jpeg", import.meta.url).toString();

export function Footer() {
  function scrollToTop() {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  return (
    <footer className="relative border-t border-[var(--gold)]/15 bg-[var(--navy-deep)]/80 pt-16 pb-8">
      {/* Back to top */}
      <button
        onClick={scrollToTop}
        aria-label="Back to top"
        className="absolute -top-5 left-1/2 -translate-x-1/2 h-10 w-10 rounded-full glass-card grid place-items-center text-gold spring-hover hover:-translate-y-1 hover:border-[var(--gold)]/40 transition-all"
      >
        <ArrowUp size={16} />
      </button>

      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
        {/* Top row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 sm:gap-10 items-start mb-10 sm:mb-12">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-12 w-12 rounded-full overflow-hidden ring-2 ring-[var(--gold)]/40">
                <img src={logo} alt="Mononites logo" className="h-full w-full object-cover" />
              </div>
              <div>
                <div className="font-display text-xl text-gold">mononites</div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
                  Food Services
                </div>
              </div>
            </div>
            <p className="text-sm text-muted-foreground max-w-xs font-light leading-relaxed">
              Authentic South Indian vegetarian catering and cafeteria management for
              India's most discerning workplaces.
            </p>
            {/* Social links */}
            <div className="flex items-center gap-4 mt-6">
              {[
                { icon: Instagram, label: "Instagram", href: "https://www.instagram.com/mononites_food_services?igsh=MWFneXRycmVsdDBoNw%3D%3D&utm_source=qr" },
                { icon: Facebook, label: "Facebook", href: "https://www.facebook.com/profile.php?id=61560665518725&mibextid=wwXIfr&rdid=caSKvQNSMWcLbg5M&share_url=https%3A%2F%2Fwww.facebook.com%2Fshare%2F1KCKutHgiY%2F%3Fmibextid%3DwwXIfr%26ref%3D1#" },
              ].map(({ icon: Icon, label, href }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="h-9 w-9 rounded-full border border-[var(--gold)]/20 grid place-items-center text-muted-foreground hover:text-gold hover:border-[var(--gold)]/50 transition-all spring-hover hover:-translate-y-0.5"
                >
                  <Icon size={14} />
                </a>
              ))}
              <a
                href="https://wa.me/919884922299"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="WhatsApp"
                className="h-9 w-9 rounded-full border border-[var(--gold)]/20 grid place-items-center text-muted-foreground hover:text-gold hover:border-[var(--gold)]/50 transition-all spring-hover hover:-translate-y-0.5"
              >
                <WhatsAppIcon size={14} />
              </a>
            </div>
          </div>

          {/* Links */}
          <div className="flex sm:justify-end md:justify-center">
            <div>
              <div className="text-xs uppercase tracking-[0.35em] text-muted-foreground mb-5">
                Navigate
              </div>
              <ul className="space-y-3">
                {[
                  { label: "About",        href: "#about" },
                  { label: "Our Services", href: "#services" },
                  { label: "Directors",    href: "#team" },
                  { label: "Location",     href: "#location" },
                  { label: "Contact",      href: "#contact" },
                ].map(({ label, href }) => (
                  <li key={label}>
                    <a
                      href={href}
                      className="text-sm text-muted-foreground hover:text-gold transition-colors"
                    >
                      {label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Stay in touch */}
          <div>
            <div className="text-xs uppercase tracking-[0.35em] text-muted-foreground mb-5">
              Stay in touch
            </div>
            <p className="text-sm text-muted-foreground font-light mb-4">
              Follow the latest updates in our channel.
            </p>
            <a
              href="https://whatsapp.com/channel/0029VaCHTS2KmCPPz3hDY92J"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-500 transition-all hover:-translate-y-0.5 active:translate-y-0"
            >
              <WhatsAppIcon size={16} />
              Join here
            </a>
          </div>
        </div>

        <div className="hairline mb-6" />

        {/* Bottom row */}
        <div className="flex flex-col md:flex-row justify-between gap-3 text-xs text-muted-foreground">
          <div>
            © {new Date().getFullYear()}{" "}
            <a
              href="https://korsha.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gold hover:text-gold/80 transition-colors"
            >
              Korsha.ai
            </a>
            . All rights reserved.
          </div>
          <div className="italic font-light text-muted-foreground/70">
            Crafted with pride · Pure vegetarian · Chennai
          </div>
          {/* <div className="uppercase tracking-[0.3em]">FSSAI · ISO 22000</div> */}
        </div>
      </div>
    </footer>
  );
}
